import { ethers } from "ethers";

import AxiosFactory from "./http.service";
import ApiRoutes from "../config/api.routes";
import { MainChainID } from "../utils/constants";
import { ecoForwarderContractAddress, getMetaMaskSigner, getReadOnlyContract, getReadOnlyProvider } from "./contract.service";

// Gasless relay client (V9.1) — verifier / attestor / holder actions are signed as
// EIP-712 ForwardRequests and POSTed to the backend's /relay endpoint instead of being
// sent as wallet transactions. The backend pays gas and submits via EcoForwarder.execute.
//
// Two-lane rule (FRONTEND_INTEGRATION_GUIDE §3): only allowlisted verifier/attestor/holder/GTA
// selectors — plus the patron overcharge/transmute lane, which is open to any wallet — go
// through here. Guardian minting, patron bidding, reward claims and *any* ERC-20 `approve`
// stay direct wallet transactions. A relayed `approve` would set the forwarder's allowance,
// not the user's.

// EIP-712 domain — must match the deployed EcoForwarder byte-for-byte; the backend
// re-verifies the signature against the contract and 403s on any mismatch. These constants
// are the offline default: buildForwardRequest prefers the domain the backend serves from
// GET /relay/nonce/:address, which is generated from the same config the relay validates
// against and so cannot drift from the deployed forwarder.
export const RELAY_DOMAIN = {
  name: "EcoForwarder",
  version: "1",
  chainId: MainChainID,
  verifyingContract: ecoForwarderContractAddress,
} as const;

export const FORWARD_REQUEST_TYPES: Record<string, ethers.TypedDataField[]> = {
  ForwardRequest: [
    { name: "from", type: "address" },
    { name: "to", type: "address" },
    { name: "value", type: "uint256" },
    { name: "gas", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
    { name: "data", type: "bytes" },
  ],
};

// Fixed request parameters agreed with the backend (FRONTEND_UPDATE_PLAN §4.2).
export const RELAY_GAS_LIMIT = 2_000_000;
export const RELAY_DEADLINE_SECONDS = 60 * 60; // 1h — the backend rejects deadline 0 as already-expired

// JSON-safe shape POSTed to /relay. Every uint256 travels as a decimal string — uint256
// can exceed Number.MAX_SAFE_INTEGER and bigint doesn't survive JSON.stringify. ethers
// accepts the strings for both signTypedData and the on-chain verify() call.
export interface ForwardRequest {
  from: string;
  to: string;
  value: string;
  gas: string;
  nonce: string;
  deadline: string;
  data: string;
}

export interface RelayResult {
  txHash: string;
  returndata: string;
}

// Thrown when the relayed call reverted on-chain. The backend returns this as a *200*
// with success:false (a legitimate on-chain rejection, not a relay error), so it never
// surfaces as an HTTP error — callers must not treat a resolved POST as success.
export class RelayRevertError extends Error {
  txHash?: string;
  returndata: string;
  // The custom error's name as decoded by the backend (e.g. "NotBoundVerifier"). Stable
  // enough to branch on, unlike the message. Undefined when we decoded locally instead.
  errorName?: string;

  constructor(reason: string, returndata: string, txHash?: string, errorName?: string) {
    super(reason);
    this.name = "RelayRevertError";
    this.returndata = returndata;
    this.txHash = txHash;
    this.errorName = errorName;
  }
}

// Thrown on a 429. Every accepted relay spends gas from the backend's hot wallet, so the
// endpoint is capped at 10 requests/minute per wallet and 30/minute per IP. Nothing reached
// the chain — the user just has to wait, which is worth saying differently from a failure.
export class RelayRateLimitError extends Error {
  constructor(message?: string) {
    super(message ?? "Too many gasless requests — please wait a minute and try again.");
    this.name = "RelayRateLimitError";
  }
}

// The decoded revert the backend returns alongside success:false. It decodes returndata
// against the custom errors of *all* relay targets (a call to Bank can revert inside Core)
// plus Error(string) / Panic(uint256) — strictly better than decoding against the single
// target interface we hold locally.
export interface RelayDecodedError {
  selector?: string;
  name?: string;
  args?: Record<string, unknown>;
  message?: string;
}

// Decode revert returndata into something a user can read: Error(string), Panic(uint256),
// or a custom error on the target contract's interface. Falls back to the raw hex.
export const decodeRelayRevert = (returndata: string, iface?: ethers.Interface): string => {
  if (!returndata || returndata === "0x") {return "Transaction reverted without a reason";}
  try {
    if (returndata.startsWith("0x08c379a0")) {
      // Error(string)
      const [reason] = ethers.AbiCoder.defaultAbiCoder().decode(["string"], "0x" + returndata.slice(10));
      return reason;
    }
    if (returndata.startsWith("0x4e487b71")) {
      // Panic(uint256)
      const [code] = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], "0x" + returndata.slice(10));
      return `Transaction panicked (code ${code})`;
    }
    if (iface) {
      const parsed = iface.parseError(returndata);
      if (parsed) {
        const args = parsed.args.map((a) => String(a)).join(", ");
        return args ? `${parsed.name}(${args})` : parsed.name;
      }
    }
  } catch {
    // fall through to raw hex
  }
  return `Transaction reverted (${returndata})`;
};

// Signed typed-data prompts are opaque in MetaMask and don't carry a chain switch the way
// a transaction does, so enforce the network guard ourselves before asking for a signature.
export const ensureCorrectChain = async (signer: ethers.Signer) => {
  const network = await signer.provider?.getNetwork();
  if (network && Number(network.chainId) !== MainChainID) {
    throw new Error(`Wrong network — please switch your wallet to chain ${MainChainID} (BNB Smart Chain Testnet)`);
  }
};

// A request plus the exact domain/types it must be signed with. The domain is not always the
// module constant — see buildForwardRequest — so it has to travel with the request.
export interface SignableForwardRequest {
  req: ForwardRequest;
  domain: ethers.TypedDataDomain;
  types: Record<string, ethers.TypedDataField[]>;
}

interface RelayNonceResponse {
  address: string;
  nonce: string;
  domain: ethers.TypedDataDomain;
  types: Record<string, ethers.TypedDataField[]>;
  gasLimit: number;
}

// GET /relay/nonce/:address — nonce plus the signing domain, generated from the same config
// the relay validates against. Returns null (rather than throwing) on any failure so the
// caller can fall back to reading the chain directly; a backend outage shouldn't be the thing
// that stops a gasless action the chain would happily accept.
export const fetchRelayNonce = async (address: string): Promise<RelayNonceResponse | null> => {
  try {
    const axios = new AxiosFactory().axios;
    const data = await axios.get(ApiRoutes.relayNonce(address)).then((res) => res.data);
    if (!data?.nonce || !data?.domain || !data?.types) {return null;}
    return data as RelayNonceResponse;
  } catch {
    return null;
  }
};

// Assemble a ForwardRequest for `from` calling `data` on `to`. Prefers the backend's
// nonce endpoint (which also pins the domain/types/gas cap); falls back to reading
// EcoForwarder.getNonce on the read-only provider with our own constants. Either way there
// is no wallet popup — the signature prompt comes later, in signForwardRequest.
export const buildForwardRequest = async (from: string, to: string, data: string): Promise<SignableForwardRequest> => {
  const served = await fetchRelayNonce(from);
  if (served) {
    return {
      req: {
        from,
        to,
        value: "0",
        gas: String(served.gasLimit ?? RELAY_GAS_LIMIT),
        nonce: served.nonce,
        deadline: String(Math.floor(Date.now() / 1000) + RELAY_DEADLINE_SECONDS),
        data,
      },
      domain: served.domain,
      types: served.types,
    };
  }

  const forwarder = getReadOnlyContract("ecoForwarder");
  if (!forwarder) {throw new Error("EcoForwarder contract not found");}
  const nonce: bigint = await forwarder.getNonce(from);
  return {
    req: {
      from,
      to,
      value: "0",
      gas: String(RELAY_GAS_LIMIT),
      nonce: nonce.toString(),
      deadline: String(Math.floor(Date.now() / 1000) + RELAY_DEADLINE_SECONDS),
      data,
    },
    domain: RELAY_DOMAIN,
    types: FORWARD_REQUEST_TYPES,
  };
};

// Domain/types default to the module constants so this stays callable with a bare request
// (the offline signing tests do exactly that).
export const signForwardRequest = (
  signer: ethers.Signer,
  req: ForwardRequest,
  domain: ethers.TypedDataDomain = RELAY_DOMAIN,
  types: Record<string, ethers.TypedDataField[]> = FORWARD_REQUEST_TYPES
): Promise<string> => {
  return signer.signTypedData(domain, types, req);
};

// POST { req, signature } to the backend. Uses AxiosFactory so the auth header rides along.
// 4xx means the backend rejected the request before touching the chain (bad target address,
// selector not allowlisted, expired deadline, bad signature, or role pre-check failed).
export const postRelay = async (req: ForwardRequest, signature: string, iface?: ethers.Interface): Promise<RelayResult> => {
  const axios = new AxiosFactory().axios;
  let data: { success: boolean; returndata: string; txHash: string; error?: RelayDecodedError };
  try {
    data = await axios.post(ApiRoutes.relay, { req, signature }).then((res) => res.data);
  } catch (e: any) {
    if (e?.response?.status === 429) {
      throw new RelayRateLimitError(e?.response?.data?.message);
    }
    const backendMessage = e?.response?.data?.message ?? e?.response?.data?.error;
    throw new Error(backendMessage ?? e?.message ?? "Relay request failed");
  }
  // A 200 whose body isn't the documented shape (proxy interference, API drift) must not be
  // mistaken for an on-chain revert — that error path implies the request actually executed.
  if (typeof data?.success !== "boolean") {
    throw new Error("Unexpected /relay response — the relay endpoint may be misconfigured or unreachable");
  }
  if (!data.success) {
    // Prefer the backend's decode: it covers the custom errors of every relay target, while
    // `iface` is only the one contract we addressed. Fall back to local decoding when the
    // field is absent (older backend, or a body that lost it in transit).
    throw new RelayRevertError(
      data.error?.message || decodeRelayRevert(data.returndata, iface),
      data.returndata,
      data.txHash,
      data.error?.name
    );
  }
  return { txHash: data.txHash, returndata: data.returndata };
};

export interface RelayEventConfirmation {
  // Contract that emits the confirming event — its address and interface.
  address: string;
  iface: ethers.Interface;
  eventName: string;
}

// Wait for the relayed transaction to mine on the read-only provider and confirm the
// expected event was emitted by the target contract. EcoForwarder.execute does not bubble
// inner reverts as a transaction failure, so a mined tx alone is NOT success — only the
// event proves the inner call ran. Never report "done" to the user before this resolves.
export const waitForRelayEvent = async (
  txHash: string,
  confirmation: RelayEventConfirmation,
  timeoutMs = 120_000
): Promise<ethers.Result> => {
  const provider = getReadOnlyProvider();
  const receipt = await provider.waitForTransaction(txHash, 1, timeoutMs);
  if (!receipt) {throw new Error(`Timed out waiting for relay transaction ${txHash}`);}
  if (receipt.status === 0) {throw new Error(`Relay transaction ${txHash} failed on-chain`);}

  const target = confirmation.address.toLowerCase();
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== target) {continue;}
    try {
      const parsed = confirmation.iface.parseLog({ topics: [...log.topics], data: log.data });
      if (parsed?.name === confirmation.eventName) {return parsed.args;}
    } catch {
      // log belongs to another interface — keep scanning
    }
  }
  throw new Error(
    `Relay transaction mined but ${confirmation.eventName} was not emitted — the contract call may not have taken effect`
  );
};

export type RelayPhase = "signing" | "relaying" | "confirming";

// Global phase events so one app-level status overlay can render signing/relaying progress
// (and the "no gas needed" affordance) for every relay action, without each screen wiring
// its own dialog. `label` is the human-readable action ("Accept verification request") —
// typed-data prompts are opaque in MetaMask, so this is the signing-transparency surface.
export interface RelayPhaseEvent {
  phase: RelayPhase | "success" | "error" | "cancelled";
  label?: string;
  target?: string;
  error?: string;
}
const relayPhaseListeners = new Set<(e: RelayPhaseEvent) => void>();
export const onRelayPhase = (listener: (e: RelayPhaseEvent) => void): (() => void) => {
  relayPhaseListeners.add(listener);
  return () => {
    relayPhaseListeners.delete(listener);
  };
};
const emitRelayPhase = (e: RelayPhaseEvent) => relayPhaseListeners.forEach((listener) => listener(e));

// MetaMask user rejection (4001 / ACTION_REJECTED) — treat as a cancel, not an error.
export const isUserRejection = (e: any): boolean =>
  e?.code === 4001 || e?.code === "ACTION_REJECTED" || e?.info?.error?.code === 4001;

export interface RelayCallParams {
  // Target contract address (Admin / Core / Bank / Overcharge / Governance / EcoChallenge —
  // must be on the backend's allowlist; anything else is rejected with a 400 before the
  // chain is touched).
  to: string;
  iface: ethers.Interface;
  fn: string;
  args: any[];
  // Event that proves the inner call took effect (e.g. DeedAttested on Bank).
  // Strongly recommended — without it success means only "backend reported success".
  confirmation?: RelayEventConfirmation;
  // Human-readable action shown by the relay status UI while the user signs.
  label?: string;
  onPhase?: (phase: RelayPhase) => void;
}

// The imperative relay path: encode → network guard → nonce → EIP-712 sign → POST →
// wait for the confirming event. One typed-data signature, no gas, no transaction.
// Throws on failure (use isUserRejection to distinguish a cancel). useRelayAction wraps
// this with React state — prefer the hook in components that render per-phase UI; call
// this directly from existing imperative try/catch handlers.
export const executeRelay = async ({ to, iface, fn, args, confirmation, label, onPhase }: RelayCallParams): Promise<RelayResult> => {
  const phase = (p: RelayPhase) => {
    onPhase?.(p);
    emitRelayPhase({ phase: p, label, target: to });
  };
  try {
    phase("signing");
    const signer = await getMetaMaskSigner();
    await ensureCorrectChain(signer);
    const from = await signer.getAddress();
    const data = iface.encodeFunctionData(fn, args);
    const { req, domain, types } = await buildForwardRequest(from, to, data);
    const signature = await signForwardRequest(signer, req, domain, types);

    phase("relaying");
    const result = await postRelay(req, signature, iface);

    if (confirmation) {
      phase("confirming");
      await waitForRelayEvent(result.txHash, confirmation);
    }
    emitRelayPhase({ phase: "success", label, target: to });
    return result;
  } catch (e: any) {
    emitRelayPhase(
      isUserRejection(e)
        ? { phase: "cancelled", label, target: to }
        : { phase: "error", label, target: to, error: e?.message }
    );
    throw e;
  }
};

// Convenience for call sites targeting our own contracts: looks up the address + interface
// from the factory and confirms via an event emitted by that same contract. The type union
// is deliberately the six relay-allowlisted targets — everything else is the direct lane.
// Overcharge is included because the patron overcharge/transmute lane is gasless too: those
// functions are open to any wallet and ownership is enforced on chain. V10 adds Governance
// (judgePreMint) and EcoChallenge (raise / respond / withdraw / attest / recordOutcome): the
// whole adjudication surface moved off Bank and Core onto those two contracts.
export const relayContractCall = (
  contractType: "ecofutures-admin" | "ecofutures-core" | "ecofutures-bank" | "overcharge" | "governance" | "challenge",
  fn: string,
  args: any[],
  confirmationEvent: string,
  label?: string
): Promise<RelayResult> => {
  const contract = getReadOnlyContract(contractType);
  if (!contract) {throw new Error(`${contractType} contract not found`);}
  const to = contract.target as string;
  return executeRelay({
    to,
    iface: contract.interface,
    fn,
    args,
    confirmation: { address: to, iface: contract.interface, eventName: confirmationEvent },
    label,
  });
};
